import i18next from "src/i18n/config";
import RouteService from "src/services/routeService";
import { transformErrorResponse } from "src/store/helpers";
import { BasicApiResponse, ListResponse, PaginatedResponse } from "src/types";

import { workspacesApi } from "@dashboard/workspaces/api";
import { BaseQueryFn, EndpointBuilder } from "@reduxjs/toolkit/query";
import { toBackendPageIndex } from "@shared/table/Tanstack.utils";

import { MdmCommand } from "../../types/commands";

type CommandsTag = "DeviceCommands" | "GroupCommands";

type CommandsBuilder = EndpointBuilder<BaseQueryFn, CommandsTag, "commandsApi">;

type CommandPage = {
    list: MdmCommand[];
    count: number;
};

type CommandPageParams = {
    pageIndex: number;
    pageSize: number;
    sortCriteria: string;
    sortDirection: string;
};

type GetDeviceCommandsParams = CommandPageParams & { deviceId: number };
type GetGroupCommandsParams = CommandPageParams & { groupId: number };

function toPageRequest({ pageIndex, pageSize, sortCriteria, sortDirection }: CommandPageParams) {
    return { pageIndex: toBackendPageIndex(pageIndex), pageSize, sortCriteria, sortDirection };
}

function toCommandPage(response: PaginatedResponse<MdmCommand>): CommandPage {
    return { list: response.list, count: response.count };
}

function getDeviceCommands(builder: CommandsBuilder) {
    return builder.query<CommandPage, GetDeviceCommandsParams>({
        providesTags: (_result, _error, arg) => [{ type: "DeviceCommands" as const, id: arg.deviceId }],
        transformErrorResponse: (err) => transformErrorResponse(err, i18next.t("common:error.technicalDifficulties.message")),
        transformResponse: toCommandPage,
        query: async ({ deviceId, ...page }) => {
            const base = await RouteService.getDevicesRoute();
            return { url: `${base}${deviceId}/commands`, params: toPageRequest(page) };
        },
    });
}

function createDeviceCommand(builder: CommandsBuilder) {
    return builder.mutation<MdmCommand[], { deviceId: number; json: Record<string, unknown> }>({
        transformResponse: (response: ListResponse<MdmCommand>) => response.list,
        query: async ({ deviceId, json }) => {
            const base = await RouteService.getDevicesRoute();
            return {
                url: `${base}${deviceId}/commands`,
                method: "POST",
                data: { payload: json },
                headers: { "content-type": "application/json" },
            };
        },
        onQueryStarted: async (_arg, { queryFulfilled, dispatch }) => {
            try {
                await queryFulfilled;
                dispatch(workspacesApi.util.invalidateTags(["WorkspaceFeatures"]));
            } catch {
                // the command never reached the backend, so nothing was consumed
            }
        },
    });
}

function deleteDeviceCommand(builder: CommandsBuilder) {
    return builder.mutation<BasicApiResponse, { deviceId: number; commandId: number }>({
        invalidatesTags: (_result, error, arg) => (error ? [] : [{ type: "DeviceCommands" as const, id: arg.deviceId }]),
        query: async ({ deviceId, commandId }) => {
            const base = await RouteService.getDevicesRoute();
            return { url: `${base}${deviceId}/commands/${commandId}`, method: "DELETE" };
        },
        onQueryStarted: async (_arg, { queryFulfilled, dispatch }) => {
            try {
                await queryFulfilled;
                dispatch(workspacesApi.util.invalidateTags(["WorkspaceFeatures"]));
            } catch {
                // the command never reached the backend, so nothing was consumed
            }
        },
    });
}

function getGroupCommands(builder: CommandsBuilder) {
    return builder.query<CommandPage, GetGroupCommandsParams>({
        providesTags: (_result, _error, arg) => [{ type: "GroupCommands" as const, id: arg.groupId }],
        transformErrorResponse: (err) => transformErrorResponse(err, i18next.t("common:error.technicalDifficulties.message")),
        transformResponse: toCommandPage,
        query: async ({ groupId, ...page }) => {
            const base = await RouteService.getGroupsRoute();
            return { url: `${base}${groupId}/commands`, params: toPageRequest(page) };
        },
    });
}

function createGroupCommand(builder: CommandsBuilder) {
    return builder.mutation<MdmCommand[], { groupId: number; json: Record<string, unknown> }>({
        transformResponse: (response: ListResponse<MdmCommand>) => response.list,
        query: async ({ groupId, json }) => {
            const base = await RouteService.getGroupsRoute();
            return {
                url: `${base}${groupId}/commands`,
                method: "POST",
                data: { payload: json },
                headers: { "content-type": "application/json" },
            };
        },
        onQueryStarted: async (_arg, { queryFulfilled, dispatch }) => {
            try {
                const { data } = await queryFulfilled;
                // A group with no devices of its own answers OK with an empty list, and the list carries one
                // row per device actually reached, so an empty one means nothing was consumed.
                if (data.length) {
                    dispatch(workspacesApi.util.invalidateTags(["WorkspaceFeatures"]));
                }
            } catch {
                // the command never reached the backend
            }
        },
    });
}

function deleteGroupCommand(builder: CommandsBuilder) {
    return builder.mutation<BasicApiResponse, { groupId: number; commandId: number }>({
        invalidatesTags: (_result, error, arg) => (error ? [] : [{ type: "GroupCommands" as const, id: arg.groupId }]),
        query: async ({ groupId, commandId }) => {
            const base = await RouteService.getGroupsRoute();
            return { url: `${base}${groupId}/commands/${commandId}`, method: "DELETE" };
        },
        onQueryStarted: async (_arg, { queryFulfilled, dispatch }) => {
            try {
                await queryFulfilled;
                dispatch(workspacesApi.util.invalidateTags(["WorkspaceFeatures"]));
            } catch {
                // the command never reached the backend, so nothing was consumed
            }
        },
    });
}

export default {
    createDeviceCommand,
    createGroupCommand,
    deleteDeviceCommand,
    deleteGroupCommand,
    getDeviceCommands,
    getGroupCommands,
};
